<template>
	<div class="content">
		<section
			id="hero"
			class="container-expanded container band"
		>
			<h1>Aligning Effective<br />Instruction and Relationships</h1>
		</section>
		<section
			id="intro"
			class="container-expanded container cols band"
		>
			<div class="col col-2" />
			<div class="col col-center">
				<h3 class="text-center">Teaching in Technicolour: Practical tools to help schools align direct/explicit instruction with strong relationships.</h3>
				<p>Schools have long grappled with the conundrum between delivering explicit, effective instruction or cultivating the affective relationships that make learning stick.</p>
				<p>But the good news is – you don’t have to choose.</p>
				<p>We have developed practical resources that reflect current research and existing system directives, and will support educators in achieving both effective instruction and healthy relationships – the ultimate experience in every classroom.</p>
			</div>
			<div class="col col-2" />
		</section>

		<section
			id="why-whitepaper"
			class="cols container-expanded container-no-pad"
		>
			<div
				id="why"
				class="col col-5"
			>
				<div class="col-inner">
					<h3>Why this matters</h3>
					<p>There is a perceived tension between effective/explicit instruction (EI/DI) and restorative, relationship-centred practice (RP). But in reality, when you align them, classrooms become calmer, engagement climbs, and achievement grows – because students feel safe, connected and challenged.</p>
					<p>These resources reflect current research and system directives to help you deliver both: precise teaching and the healthy relationships that enhance every interaction. That’s what we call Teaching in Technicolour.</p>
				</div>
			</div>
			<div
				id="whitepaper"
				class="col col-7"
			>
				<div class="col-inner">
					<h6>Whitepaper</h6>
					<h3>Aligning Effective Instruction and Relationships</h3>
					<div class="cols">
						<div
							id="cover"
							class="col col-4"
						>
							<img
								src="@/images/aligning-effective-instruction.webp"
								alt="Aligning Effective Instruction and Relationships front cover"
							/>
							<div>
								<a
									href="https://backend.partner-portal.realschools.com.au/wp-content/uploads/2025/01/Aligning-Effective-Instruction-and-Relationships-January-2025.pdf"
									class="btn"
									target="_blank"
									download="Aligning-Effective-Instruction-and-Relationships-January-2025.pdf"
									>Download</a
								>
							</div>
						</div>
						<div class="col col-8">
							<p>This whitepaper will support you in understanding the elements of direct and explicit instruction that are attainable through embracing Restorative Practices.</p>
							<div class="authors">
								<p><strong>By Brenda Quayle and Trish Coelho</strong></p>
							</div>
							<div class="headshots">
								<img
									src="@/images/headshots/brenda.webp"
									alt="Brenda Quayle"
								/>
								<img
									src="@/images/headshots/trish.webp"
									alt="Trish Coelho"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>

		<section
			id="podcast"
			class="container-expanded container cols band"
		>
			<div class="col col-2" />
			<div class="col col-center">
				<h2 class="show-title">The Missing Popcorn - The Podcast</h2>
				<h6 class="show-host">Adam Voigt, in conversation with Brenda Quayle and Trish Coelho</h6>
				<div class="cols">
					<div class="col col-9">
						<div class="show-description">
							<p>This podcast will unpack the complex relationship between good teaching and relationship building, setting the scene for the ultimate experience that has learners thriving in an educational environment that delivers high support accompanied with high expectations. In achieving this educational environment, classrooms and schools become transformative in nature, where all stakeholders are equal partners in ensuring success for all.</p>
						</div>
					</div>
					<div class="col col-3">
						<div class="show-links">
							<h5 class="show-listen">Listen on</h5>
							<a
								:href="links.spotify"
								target="_blank"
							>
								<img
									src="@/images/podcast/spotify.png"
									alt="Listen on Spotify"
								/>
							</a>
							<a
								:href="links.apple"
								target="_blank"
							>
								<img
									src="@/images/podcast/apple.png"
									alt="Listen on Apple Podcasts"
								/>
							</a>
							<a
								:href="links.overcast"
								target="_blank"
							>
								<img
									src="@/images/podcast/overcast.png"
									alt="Listen on Overcast"
								/>
							</a>
						</div>
					</div>
				</div>

				<div class="podcasts">
					<div
						:id="`${episode.season}-${episode.episode}`"
						class="podcast"
						v-for="episode in episodes"
						:key="`${episode.season}-${episode.episode}`"
					>
						<h6 class="podcast-episode">Episode {{ episode.episode }} | {{ episode.mins }} mins</h6>
						<h4 class="podcast-title">{{ episode.title }}</h4>
						<div class="podcast-description">
							<p v-if="episode.showMore">{{ episode.description }}</p>
							<p v-else>{{ shortDesc(episode.description) }} <button @click="episode.showMore = true">show more</button></p>
						</div>
						<div class="podcast-links">
							<a
								:href="episode.links.spotify"
								target="_blank"
							>
								<img
									src="@/images/podcast/spotify.png"
									alt="Listen on Spotify"
								/>
							</a>
							<a
								:href="episode.links.apple"
								target="_blank"
							>
								<img
									src="@/images/podcast/apple.png"
									alt="Listen on Apple Podcasts"
								/>
							</a>
							<a
								:href="episode.links.overcast"
								target="_blank"
							>
								<img
									src="@/images/podcast/overcast.png"
									alt="Listen on Overcast"
								/>
							</a>
						</div>
					</div>
				</div>
			</div>
			<div class="col col-2" />
		</section>
	</div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import heroImage from '@/images/brenda-workshop.webp';
import { wrapBackgroundImageUrlToCss } from '@/helpers';
import type { Resource } from '@/interfaces/resourceInterface';

export default defineComponent({
	props: {
		resource: {
			type: Object as PropType<Resource>,
			required: true,
		},
	},
	data() {
		return {
			links: {
				spotify: 'https://open.spotify.com/show/0SrOI9h4QqRhYnd1WKoMzo',
				apple: 'https://podcasts.apple.com/au/podcast/the-missing-popcorn-podcast/id1840050919',
				overcast: 'https://overcast.fm/itunes1840050919/the-missing-popcorn-podcast',
			},
			episodes: [
				{
					season: 1,
					episode: 1,
					title: 'Reclaiming the Joy of Teaching and Learning',
					description: 'In this inaugural episode of The Missing Popcorn, hosts Adam Voigt, Trish Coelho, and Brenda Quayle explore the vital connection between effective instruction and meaningful relationships in education. They discuss the importance of reclaiming teacher agency, the metaphor of popcorn in relation to teaching, and the necessity of balancing relationships with rigorous instruction. The conversation emphasises the human elements of education, the costs of isolation, and the power of kindness in fostering a supportive learning environment.',
					mins: 28,
					links: {
						spotify: 'https://open.spotify.com/episode/2vdnY7GMaJ15poHrrO1yf1',
						apple: 'https://podcasts.apple.com/au/podcast/episode-1-reclaiming-the-joy-of-teaching-and-learning/id1840050919',
						overcast: 'https://overcast.fm/+BRjrdabA6w',
					},
					showMore: false,
				},
				{
					season: 1,
					episode: 2,
					title: 'The Ultimate Feature Film',
					description: 'In this episode of The Missing Popcorn, Adam Voigt, Trish Coelho, and Brenda Quayle explore the intricate relationship between effective instruction and strong relational practices in education. They discuss the importance of creating a safe and engaging learning environment, the role of restorative practices in enhancing academic performance, and the need for teachers to adapt their methods for a new generation of learners. The conversation emphasises the significance of vulnerability in teaching and the necessity of building meaningful relationships with students to foster a rich and layered educational experience.',
					mins: 24,
					links: {
						spotify: 'https://open.spotify.com/episode/231PjhUVj1vOZnEZ87XTlr',
						apple: 'https://podcasts.apple.com/au/podcast/episode-2-teaching-the-ultimate-feature-film/id1840050919',
						overcast: 'https://overcast.fm/+BRjrcQW1vM',
					},
					showMore: false,
				},
				{
					season: 1,
					episode: 3,
					title: 'Beyond The Script',
					description: 'In this episode of The Missing Popcorn, Adam Voigt engages with Trish Coelho and Brenda Quayle to explore effective teaching practices that reclaim joy in education. They discuss the 4T model, which emphasises the importance of strong relationships and effective instruction. The conversation highlights the need for teachers to reflect on their practices, embrace imperfection, and focus on student engagement. Through practical examples, they illustrate how building relationships and providing constructive feedback can enhance learning experiences and save time for educators.',
					mins: 29,
					links: {
						spotify: 'https://open.spotify.com/episode/1k7skWNIQxK5UMEtNPYokD',
						apple: 'https://podcasts.apple.com/au/podcast/episode-3-beyond-the-script/id1840050919',
						overcast: 'https://overcast.fm/+BRjreVIDvw',
					},
					showMore: false,
				},
				{
					season: 1,
					episode: 4,
					title: 'Where Learning Comes Alive',
					description: 'In this episode, Adam, Trish, and Brenda explore the essential elements of creating safe and engaging learning environments. They discuss the balance between safety and robustness in classrooms, the impact of physical space on learning, and the importance of routines and relationships in fostering a positive educational experience. The conversation emphasises the need for teachers to be proactive in designing their classrooms and interactions to promote student engagement and emotional safety.',
					mins: 25,
					links: {
						spotify: 'https://open.spotify.com/episode/54qti6NAUNKyiohs3a41kV',
						apple: 'https://podcasts.apple.com/au/podcast/episode-4-where-learning-comes-alive/id1840050919',
						overcast: 'https://overcast.fm/+BRjrdTuFAI',
					},
					showMore: false,
				},
				{
					season: 1,
					episode: 5,
					title: 'Rewriting the Script - Teaching in Technicolour',
					description: 'In this episode of The Missing Popcorn, Adam, Brenda, and Trish explore the essence of impactful instruction in modern classrooms. They discuss the importance of relationships, the role of mistakes in learning, and the need for a shift from compliance to engagement. The conversation emphasises the significance of understanding students\' needs, fostering a supportive environment, and using language that promotes connection and curiosity. Ultimately, the episode advocates for a teaching approach that is vibrant and engaging, akin to "teaching in Technicolour."',
					mins: 28,
					links: {
						spotify: 'https://open.spotify.com/episode/3ilvAVyrqjQuG5OFT3r2gT',
						apple: 'https://podcasts.apple.com/au/podcast/episode-5-rewriting-the-script-teaching-in-technicolour/id1840050919',
						overcast: 'https://overcast.fm/+BRjrcZBqsc',
					},
					showMore: false,
				},
				{
					season: 1,
					episode: 6,
					title: 'Beyond the Credits - What Really Matters in Classrooms',
					description: 'This conversation explores the dynamic interplay between effective instruction and high-quality relationships in schools, emphasising the role of school leaders in fostering a positive educational environment. Trish Coelho and Brenda Quayle share insights on modelling restorative practices, building a supportive school culture, and the long-term benefits of these approaches for both students and educators.',
					mins: 29,
					links: {
						spotify: 'https://open.spotify.com/episode/0E9JXZMTKw6lBr6A9yGxy3',
						apple: 'https://podcasts.apple.com/au/podcast/episode-6-beyond-the-credits-what-really-matters-in/id1840050919',
						overcast: 'https://overcast.fm/+BRjreejwhI',
					},
					showMore: false,
				},
			],
		};
	},
	computed: {
		heroimageurl() {
			return wrapBackgroundImageUrlToCss(heroImage);
		},
	},
	methods: {
		shortDesc(description: string) {
			const limit = 260;
			return description.length > limit ? description.substring(0, limit - 3) + '...' : description;
		},
	},
});
</script>

<style scoped lang="scss">
#hero {
	display: flex;
	align-items: flex-end;
	margin: 0;
	/* stylelint-disable-next-line declaration-property-value-no-unknown */
	background-image: v-bind(heroimageurl);
	background-position: center top 24%;
	background-size: cover;
	background-repeat: no-repeat;
	min-height: 400px;

	h1 {
		margin: 0;
		color: white;
		text-shadow: 2px 2px 6px #00000082;
	}

	&::before {
		position: absolute;
		opacity: 0.2;
		z-index: -1;
		inset: 0;
		background-color: black;
		content: '';
	}
}

#intro {
	background-color: rgb(var(--rs-color-brand-navy));
	color: white;

	h3 {
		color: rgb(var(--rs-color-brand-mustard));
	}
}

#why-whitepaper {
	h3 {
		margin-bottom: 0;
	}

	#why {
		background-color: #eaeef1;
	}

	#cover {
		text-align: center;

		img {
			margin-bottom: 20px;
			max-width: 170px;
		}

		.btn {
			margin-bottom: 20px;
		}
	}

	.authors {
		text-align: center;
	}

	.headshots {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 20px;

		img {
			max-width: 120px;
		}
	}

	#whitepaper {
		h6 {
			margin-bottom: 0;
		}

		h3 {
			margin-top: 0;
		}

		.cols {
			margin: 20px 0;
		}

		@media screen and (width >= 1200px) {
			.col-inner {
				padding-right: calc(2 * var(--rs-layout-content-padding));
			}
		}
	}
}

#podcast {
	background-color: rgb(var(--rs-color-brand-cherry));

	.show-title,
	.show-host,
	.show-description {
		color: white;
	}

	.show-title {
		margin: 0 0 4px 0;
	}

	.show-host {
		margin: 0 0 40px 0;
	}

	.show-links {
		.show-listen {
			color: white;
			font-weight: bold;
		}

		a {
			img {
				max-width: 60px;
			}
		}
	}

	.podcasts {
		margin-top: 40px;

		.podcast {
			margin-bottom: 30px;
			border-radius: var(--rs-border-radius);
			background-color: white;
			padding: 20px;

			.podcast-description {
				button {
					font-weight: bold;
				}
			}

			.podcast-links {
				a {
					img {
						max-width: 60px;
					}
				}
			}

			&:last-child {
				margin-bottom: 0;
			}
		}
	}

	@media screen and (width > 640px) {
		.show-description {
			padding-right: 40px;
		}

		.show-links {
			text-align: center;
		}
	}
}
</style>
